import React, { useContext, useEffect, useState } from "react";
import Header from "./Header";
import PricingCards from "./PricingCards";
import { AuthContext, API_BASE_URL } from "../App";
import "./Home.css";
import { pricingData } from "../pricingData";
import { DISCORD_URL, CONTACT_EMAIL } from "../config";
import { Link, useNavigate } from "react-router-dom";
import { trackEvent } from "../utils/mixpanel";

function Home() {
  const navigate = useNavigate();

  const [user, setUser] = useState(null);
  const { authToken, authUser } = useContext(AuthContext);

  // Simulate checking authentication (replace with your own auth logic)
  useEffect(() => {
    const token = localStorage.getItem("authToken");
    if (token) {
      // Replace with a fetch call to your backend if needed.
      setUser({ username: "John Doe", is_premium: false });
    }
  }, []);

  const handleLogout = () => {
    localStorage.removeItem("authToken");
    setUser(null);
  };

  const handleSubscribe = async () => {
    const token = localStorage.getItem("authToken");
    if (!token) {
      // Redirect to login if the user is not authenticated
      navigate("/login");
      return;
    }
    try {
      const response = await fetch(`${API_BASE_URL}/create-checkout-session`, {
        method: "POST",
      });
      const data = await response.json();
      if (data.url) {
        window.location.href = data.url; // Redirect to Stripe checkout
      }
    } catch (error) {
      console.error("Subscription error:", error);
    }
  };

  // Smooth scrolling for anchor links
  useEffect(() => {
    // Define the handler function
    const smoothScrollHandler = function (e) {
      const href = this.getAttribute("href");
      // Ensure it's a hash link potentially prefixed with / (from React Router Link)
      if (href.startsWith("/#") || href.startsWith("#")) {
        e.preventDefault();
        // Get the ID part (e.g., #features)
        const targetId = href.startsWith("/#") ? href.substring(1) : href;
        const target = document.querySelector(targetId);
        if (target) {
          // Use scrollIntoView - CSS scroll-margin-top should handle the offset
          target.scrollIntoView({ behavior: "smooth" });

          // --- OR --- Calculate offset manually (Alternative if CSS fails)
          /*
          const headerOffset = 75; // Adjust as needed (header height + buffer)
          const elementPosition = target.getBoundingClientRect().top;
          const offsetPosition = elementPosition + window.pageYOffset - headerOffset;

          window.scrollTo({
               top: offsetPosition,
               behavior: "smooth"
          });
          */
        }
      }
      // If it's a link like /pricing, let React Router handle it (do nothing here)
    };

    // Select links rendered by React Router that point to hash IDs
    const anchors = document.querySelectorAll('a[href^="/#"]'); // Adjusted selector for <Link to="/#...">

    anchors.forEach((anchor) => {
      anchor.addEventListener("click", smoothScrollHandler);
    });

    // Cleanup function
    return () => {
      anchors.forEach((anchor) => {
        // Remove the *exact same* function reference
        anchor.removeEventListener("click", smoothScrollHandler);
      });
    };
  }, []); // Empty dependency array means this runs once on mount

  return (
    <div>
      <Header isLoggedIn={!!user} user={user || {}} onLogout={handleLogout} />
      <section className="hero">
        <div className="container">
          <h1>The First Large Chess Language Model Is Coming!</h1>
          <p>
            Learn chess with human reasoning. Annotate, save, and share your
            games effortlessly with our innovative tools.
          </p>
          <div>
            <a
              href="https://chromewebstore.google.com/detail/mldmjndlhapdfkbaeeeohdabhdkgngac?utm_source=item-share-cb"
              className="btn chrome"
              target="_blank"
              rel="noopener"
              onClick={() => trackEvent("Chrome Extension Download Clicked")}
            >
              <img src="/imgs/chrome_icon.png" alt="Chrome Icon" />
              Download Chrome Extension
            </a>
            <p className="beta-note">Beta Mode</p>
          </div>
        </div>
      </section>
      <section id="features">
        <div className="container">
          <h2>Features</h2>
          <div className="features-grid">
            <div className="feature">
              <h3>Seamless Game Transcription</h3>
              <p>
                Capture every move effortlessly with our real-time transcription
                tool, ensuring every detail of your chess games is recorded
                accurately.
              </p>
            </div>
            <div className="feature">
              <h3>Cross-Platform Game Sync</h3>
              <p>
                Integrate your chess.com and lichess games into one unified
                history, giving you a complete overview of your chess journey.
              </p>
            </div>
            <div className="feature">
              <h3>Expert Game Analysis</h3>
              <p>
                Gain powerful insights with in-depth annotations and reviews
                from top chess masters—designed to help you refine your
                strategy.
              </p>
            </div>
            <div className="feature">
              <h3>AI-Powered Chess Coaching (Coming Soon)</h3>
              <p>
                Elevate your game with our upcoming AI-driven coaching tool,
                engineered to uncover advanced strategies and improve your play.
              </p>
            </div>
          </div>
        </div>
      </section>
      <section id="pricing">
        <div className="container">
          <h2>Pricing</h2>
          <div className="pricing-cards">
            <PricingCards
              pricingData={pricingData}
              handleSubscribe={handleSubscribe}
            ></PricingCards>
          </div>
        </div>
      </section>
      <section id="about">
        <div className="container">
          <h2>About Grandmaster AI</h2>
          <p>
            Grandmaster AI is developing the world's best online AI chess coach
            that uses human reasoning to elevate your game. Our tools for chess
            annotation, note saving, and game analysis are available now—with
            even more premium features coming soon.
          </p>
        </div>
      </section>
      <section id="contact">
        <div className="container">
          <h2>Contact Us</h2>
          <p>
            Have questions or feedback? Email us at{" "}
            <a href="mailto:grandmasterai.com@gmail.com">
              grandmasterai.com@gmail.com
            </a>
          </p>
        </div>
      </section>
      <section id="social" className="social-links">
        <div className="container">
          <h2>Connect with us</h2>
          <ul className="social-list">
            <li>
              <a
                href="https://bsky.app/profile/grandmasterai.bsky.social"
                target="_blank"
                rel="noopener"
              >
                Bluesky
              </a>
            </li>
            <li>
              <a
                href="https://x.com/GrandmasterAI_"
                target="_blank"
                rel="noopener"
              >
                X (Twitter)
              </a>
            </li>
            <li>
              <a
                href="https://discord.gg/ezp9Py2VKU"
                target="_blank"
                rel="noopener"
              >
                Discord (Support)
              </a>
            </li>
          </ul>
        </div>
      </section>
      <footer>
        <div className="container">
          <p>
            Questions? Join our community on{" "}
            <a href={DISCORD_URL} target="_blank" rel="noopener">
              Discord
            </a>{" "}
            or email us at{" "}
            <a href={`mailto:${CONTACT_EMAIL}`}>{CONTACT_EMAIL}</a>.
          </p>
          <Link to="/terms">Terms of Service</Link> |{" "}
          <Link to="/privacy">Privacy Policy</Link>
          <p>&copy; 2025 Grandmaster AI. All rights reserved.</p>
        </div>
      </footer>
    </div>
  );
}

export default Home;
